/**
 * Client-side OpenTelemetry initialization
 * This file is specifically for browser environments
 */

import {initializeTracing, initializeRouterInstrumentation} from './index';
import {useRouter} from 'next/router';
import {useEffect, useRef} from 'react';
import type {NextRouter} from 'next/router';

// Custom logger for consistent error handling
const logger = {
  debug: (message: string): void => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[otel-client] ${message}`);
    }
  },
  error: (message: string, error?: unknown): void => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[otel-client] ${message}`, error);
    }
  },
};

/**
 * Initialize OpenTelemetry in browser
 * @return {boolean} Success status
 */
const initOtelInBrowser = (): boolean => {
  // Make sure we're in browser environment
  if (typeof window === 'undefined') return false;

  try {
    // Initialize tracing with browser-specific configuration
    initializeTracing();
    logger.debug('OpenTelemetry initialized in browser');
    return true;
  } catch (error) {
    // Silent fail for telemetry errors
    logger.error('Failed to initialize OpenTelemetry', error);
    return false;
  }
};

/**
 * Direct function to set up router instrumentation
 * @param {NextRouter} router The Next.js router instance
 * @return {Function|undefined} Cleanup function or undefined if initialization failed
 */
export const setupRouterInstrumentation = (router: NextRouter): (() => void) | undefined => {
  if (typeof window === 'undefined' || !router) return undefined;

  try {
    // Initialize router tracking
    const cleanup = initializeRouterInstrumentation(router);
    logger.debug('Router instrumentation initialized');
    return cleanup;
  } catch (error) {
    logger.error('Failed to initialize OpenTelemetry router', error);
    return undefined;
  }
};

/**
 * React hook for router instrumentation
 * This hook should ONLY be used inside React function components
 */
export const useOtelRouterInstrumentation = (): void => {
  const router = useRouter();
  const routerRef = useRef<NextRouter>(router);
  const cleanupRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    // Store current router in ref to avoid unnecessary effect runs
    routerRef.current = router;

    if (!router) return;

    // Clean up previous instrumentation if it exists
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = undefined;
    }

    // Use the direct function to set up instrumentation
    cleanupRef.current = setupRouterInstrumentation(router);

    // Return cleanup function
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
    };
  }, [router]);
};

export default initOtelInBrowser;

